import { useEffect, useState, useCallback, useRef } from "react";
import { API_BASE_URL } from "../utils/config";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(
      (data && (data.detail || data.non_field_errors?.[0])) ||
      "Terjadi kesalahan server. Coba lagi."
    );
  }
  return res.status === 204 ? null : res.json();
}

function SearchableSelect({ options, value, onChange, placeholder = "Pilih..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm flex items-center justify-between text-left"
      >
        <span className={`truncate ${!selected ? "text-muted" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="w-4 h-4 shrink-0 ml-1 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-white/50 dark:border-white/15 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-lg flex flex-col max-h-56">
          <div className="p-2 border-b border-white/30 dark:border-white/10 shrink-0">
            <input
              autoFocus
              type="text"
              className="w-full rounded px-2 py-1 text-sm border border-white/50 dark:border-white/15 bg-white/50 dark:bg-slate-700/50"
              placeholder="Ketik untuk cari..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted">Tidak ditemukan</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(String(o.value));
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-brand-500/10 ${
                    String(o.value) === String(value) ? "bg-brand-500/15 font-medium" : ""
                  }`}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  name: "",
  description: "",
  output_item_id: "",
  components: [],
};

export default function PanelTemplates() {
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [compItemId, setCompItemId] = useState("");
  const [compQty, setCompQty] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const [minStockEdit, setMinStockEdit] = useState(null); // { itemId, value }
  const [minStockSaving, setMinStockSaving] = useState(new Set());

  const [role, setRole] = useState(null);
  const isSuperAdmin = role === "superadmin";

  useEffect(() => {
    apiFetch("/api/auth/me/")
      .then((data) => setRole(data?.role || null))
      .catch(() => {});
  }, []);

  const saveMinStock = async (itemId, value) => {
    const num = Math.max(0, Number(value) || 0);
    setMinStockEdit(null);
    setMinStockSaving((prev) => new Set(prev).add(itemId));
    try {
      await apiFetch(`/api/items/${itemId}/`, {
        method: "PATCH",
        body: JSON.stringify({ minimum_stock: num }),
      });
      setTemplates((prev) =>
        prev.map((t) => ({
          ...t,
          components: t.components.map((c) =>
            c.item_id === itemId ? { ...c, minimum_stock: num } : c
          ),
        }))
      );
    } catch (e) {
      alert(e.message);
    } finally {
      setMinStockSaving((prev) => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tmpls, its] = await Promise.all([
        apiFetch("/api/panel-templates/"),
        apiFetch("/api/items/?page_size=9999&status=active"),
      ]);
      setTemplates(tmpls || []);
      setItems((its?.results || its || []).filter((i) => i.is_active));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setCompItemId("");
    setCompQty(1);
    setShowForm(true);
  };

  const openEdit = (tmpl) => {
    setEditingId(tmpl.id);
    setForm({
      name: tmpl.name,
      description: tmpl.description || "",
      output_item_id: String(tmpl.output_item_id),
      components: tmpl.components.map((c) => ({
        item_id: c.item_id,
        item_code: c.item_code,
        item_name: c.item_name,
        default_quantity: c.default_quantity,
      })),
    });
    setFormError("");
    setCompItemId("");
    setCompQty(1);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const addComponent = () => {
    if (!compItemId) return;
    const item = items.find((i) => i.id === Number(compItemId));
    if (!item) return;
    if (form.components.some((c) => c.item_id === item.id)) {
      setFormError("Komponen ini sudah ada.");
      return;
    }
    setFormError("");
    setForm((prev) => ({
      ...prev,
      components: [
        ...prev.components,
        { item_id: item.id, item_code: item.code, item_name: item.name, default_quantity: compQty },
      ],
    }));
    setCompItemId("");
    setCompQty(1);
  };

  const removeComponent = (itemId) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.filter((c) => c.item_id !== itemId),
    }));
  };

  const updateCompQty = (itemId, qty) => {
    setForm((prev) => ({
      ...prev,
      components: prev.components.map((c) =>
        c.item_id === itemId ? { ...c, default_quantity: Number(qty) } : c
      ),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name.trim()) return setFormError("Nama template wajib diisi.");
    if (!form.output_item_id) return setFormError("Pilih item panel output.");
    if (form.components.length === 0) return setFormError("Minimal 1 komponen.");
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description,
        output_item_id: Number(form.output_item_id),
        components: form.components.map((c) => ({
          item_id: c.item_id,
          default_quantity: c.default_quantity,
        })),
      };
      if (editingId) {
        await apiFetch(`/api/panel-templates/${editingId}/`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch("/api/panel-templates/", {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      closeForm();
      fetchAll();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openDuplicate = async (tmpl) => {
    try {
      const newTmpl = await apiFetch(`/api/panel-templates/${tmpl.id}/duplicate/`, { method: "POST" });
      await fetchAll();
      setEditingId(newTmpl.id);
      setForm({
        name: newTmpl.name,
        description: newTmpl.description || "",
        output_item_id: String(newTmpl.output_item_id),
        components: newTmpl.components.map((c) => ({
          item_id: c.item_id,
          item_code: c.item_code,
          item_name: c.item_name,
          default_quantity: c.default_quantity,
        })),
      });
      setFormError("");
      setCompItemId("");
      setCompQty(1);
      setShowForm(true);
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDeactivate = async (tmpl) => {
    if (!window.confirm(`Nonaktifkan template "${tmpl.name}"?`)) return;
    try {
      await apiFetch(`/api/panel-templates/${tmpl.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: false }),
      });
      fetchAll();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleReactivate = async (tmpl) => {
    try {
      await apiFetch(`/api/panel-templates/${tmpl.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: true }),
      });
      fetchAll();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Template Perakitan Panel</h1>
        {isSuperAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
          >
            + Tambah Template
          </button>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingId
                ? form.name.startsWith("Copy of ")
                  ? "Duplikat Template — Rename & Sesuaikan"
                  : "Edit Template"
                : "Buat Template Baru"}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1">Nama Template</label>
                <input
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Contoh: Panel 14 Channel"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Deskripsi (opsional)</label>
                <textarea
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Item Panel Output (hasil rakitan)</label>
                <SearchableSelect
                  value={form.output_item_id}
                  onChange={(v) => setForm((p) => ({ ...p, output_item_id: v }))}
                  placeholder="-- Pilih item --"
                  options={items.map((i) => ({ value: i.id, label: `${i.code} - ${i.name}` }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-2">Komponen</label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <SearchableSelect
                      value={compItemId}
                      onChange={setCompItemId}
                      placeholder="-- Pilih komponen --"
                      options={items
                        .filter((i) => !form.components.some((c) => c.item_id === i.id))
                        .map((i) => ({ value: i.id, label: `${i.code} - ${i.name}` }))}
                    />
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="w-20 rounded-md border border-white/50 dark:border-white/15 px-2 py-1.5 text-sm"
                    value={compQty}
                    onChange={(e) => setCompQty(Number(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={addComponent}
                    className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700"
                  >
                    Tambah
                  </button>
                </div>
                {form.components.length > 0 && (
                  <div className="glass-table-wrap overflow-x-auto">
                    <table className="glass-table text-xs">
                      <thead>
                        <tr>
                          <th className="text-left">Kode</th>
                          <th className="text-left">Nama</th>
                          <th className="text-right">Qty Default</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.components.map((c) => (
                          <tr key={c.item_id}>
                            <td className="px-3 py-1.5">{c.item_code}</td>
                            <td className="px-3 py-1.5">{c.item_name}</td>
                            <td className="px-3 py-1.5 text-right">
                              <input
                                type="number"
                                min={1}
                                className="w-16 rounded border border-white/50 dark:border-white/15 px-1 py-0.5 text-xs text-right"
                                value={c.default_quantity}
                                onChange={(e) => updateCompQty(c.item_id, e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <button
                                type="button"
                                onClick={() => removeComponent(c.item_id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                              >
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Template list */}
      {!loading && templates.length === 0 && (
        <p className="text-sm text-muted">Belum ada template. Buat template pertama.</p>
      )}
      <div className="space-y-3">
        {templates.map((tmpl) => (
          <div key={tmpl.id} className="glass-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-sm">{tmpl.name}</span>
                  {!tmpl.is_active && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-400/20 text-gray-500">
                      Nonaktif
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted">
                  Output: {tmpl.output_item_code} – {tmpl.output_item_name}
                </p>
                {tmpl.description && (
                  <p className="text-xs text-muted mt-0.5">{tmpl.description}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setExpandedId(expandedId === tmpl.id ? null : tmpl.id)}
                  className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                >
                  {expandedId === tmpl.id ? "Tutup" : `${tmpl.components.length} komponen`}
                </button>
                {isSuperAdmin && (
                  <>
                    <button
                      onClick={() => openDuplicate(tmpl)}
                      className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                    >
                      Duplikat
                    </button>
                    <button
                      onClick={() => openEdit(tmpl)}
                      className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                    >
                      Edit
                    </button>
                    {tmpl.is_active ? (
                      <button
                        onClick={() => handleDeactivate(tmpl)}
                        className="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        Nonaktifkan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivate(tmpl)}
                        className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                      >
                        Aktifkan
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {expandedId === tmpl.id && (
              <div className="mt-3 glass-table-wrap overflow-x-auto">
                <table className="glass-table text-xs">
                  <thead>
                    <tr>
                      <th className="text-left">Kode</th>
                      <th className="text-left">Komponen</th>
                      <th className="text-right">Qty Default</th>
                      <th className="text-right">Stok Tersedia</th>
                      <th className="text-right">Min Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tmpl.components.map((c) => {
                      const qty = c.quantity_available ?? 0;
                      const isEmpty = c.minimum_stock > 0 && qty === 0;
                      const isLow = c.minimum_stock > 0 && qty > 0 && qty < c.minimum_stock;
                      const rowCls = isEmpty ? "bg-red-500/10" : isLow ? "bg-amber-500/10" : "";
                      return (
                        <tr key={c.id} className={rowCls}>
                          <td className="px-3 py-1.5">{c.item_code}</td>
                          <td className="px-3 py-1.5">
                            <span>{c.item_name}</span>
                            {isEmpty && (
                              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                </svg>
                                Stok habis
                              </span>
                            )}
                            {isLow && (
                              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                                Stok rendah
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right">{c.default_quantity}</td>
                          <td className={`px-3 py-1.5 text-right font-medium ${isEmpty ? "text-red-600 dark:text-red-400" : isLow ? "text-amber-600 dark:text-amber-400" : ""}`}>
                            {c.quantity_available ?? "-"}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {minStockSaving.has(c.item_id) ? (
                              <span className="text-muted text-[10px]">saving...</span>
                            ) : minStockEdit?.itemId === c.item_id ? (
                              <input
                                autoFocus
                                type="number"
                                min={0}
                                className="w-14 rounded border border-brand-400 px-1 py-0.5 text-xs text-right bg-white/80 dark:bg-slate-700/80"
                                value={minStockEdit.value}
                                onChange={(e) => setMinStockEdit({ itemId: c.item_id, value: e.target.value })}
                                onBlur={() => saveMinStock(c.item_id, minStockEdit.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveMinStock(c.item_id, minStockEdit.value);
                                  if (e.key === "Escape") setMinStockEdit(null);
                                }}
                              />
                            ) : (
                              <button
                                type="button"
                                title="Klik untuk set minimum"
                                onClick={() => setMinStockEdit({ itemId: c.item_id, value: c.minimum_stock ?? 0 })}
                                className={`rounded px-1.5 py-0.5 hover:bg-white/30 dark:hover:bg-white/10 transition-colors ${c.minimum_stock > 0 ? "text-strong font-medium" : "text-muted"}`}
                              >
                                {c.minimum_stock > 0 ? c.minimum_stock : "—"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
